/**
 * DL-01 — every sentence of "Download images", in one place (Christian may
 * reword). English only — the app carries no i18n layer.
 */
import type { DownloadRefusal } from "./errors.js";
import { LIMIT_BYTES, WARN_BYTES, fileWord, formatBytes } from "./guards.js";
import type { DownloadScope } from "./manifestApi.js";

export const CLI_HINT = "the CLI `aiq download`";

/** Legacy rows (lyse/v1 8.7.1, `blob_state = unknown`) carry no size: the
 *  server answers `bytes` 0 for them, so a non-zero file count with zero
 *  bytes reads "size not recorded", never "0 B". */
const SIZE_NOT_RECORDED_HINT =
  "Size not recorded — the server holds no size for these originals; make sure the drive you choose has room.";
const sizeWord = (files: number, bytes: number): string =>
  files > 0 && bytes === 0 ? "size not recorded" : formatBytes(bytes);

export const WORDS = {
  action: "Download images",
  actionAll: "Download images of all data sets",
  title: (scope: DownloadScope): string =>
    scope.kind === "da"
      ? "Download images"
      : scope.kind === "instruction"
        ? "Download images · all data sets of the instruction"
        : "Download images · all data sets of the project",
  loading: "Asking the server for the file list…",
  unsupported: `Your browser cannot write a folder on this PC. Use Chrome or Edge, or ${CLI_HINT}.`,
  summary: (files: number, bytes: number, sets: number): string =>
    `${fileWord(files)} · ${sizeWord(files, bytes)}` +
    (sets > 1 ? ` · ${String(sets)} data sets` : ""),
  skipped: (n: number): string =>
    `${String(n)} ${n === 1 ? "image is" : "images are"} not available for download (missing or withheld) and will be listed in manifest.json.`,
  fullResolution:
    "Full resolution, byte-identical to storage — the original file names, one folder per data set.",
  largeWarningTitle: `More than ${formatBytes(WARN_BYTES)}`,
  largeWarning: (bytes: number): string =>
    `This download needs ${formatBytes(bytes)}. Make sure the drive you choose has at least ${formatBytes(bytes)} free — the browser cannot check that for you.`,
  requiredBefore: (bytes: number): string =>
    bytes > 0
      ? `Required: ${formatBytes(bytes)}. Make sure the drive you choose has at least ${formatBytes(bytes)} free.`
      : SIZE_NOT_RECORDED_HINT,
  requiredAfterVerify: (bytes: number, rootName: string): string =>
    bytes > 0
      ? `${formatBytes(bytes)} to download — make sure the drive holding "${rootName}" has at least ${formatBytes(bytes)} free.`
      : `Size not recorded — make sure the drive holding "${rootName}" has room for the full-resolution originals.`,
  chooseFolder: "Choose folder and download",
  picking: "Pick the folder the images go into…",
  verifying: "Checking what is already on disk…",
  running: (done: number, total: number): string =>
    `Downloading · ${String(done)} / ${String(total)} files`,
  present: (md5: number, size: number): string => {
    const parts: string[] = [];
    if (md5 > 0) parts.push(`${fileWord(md5)} already present`);
    if (size > 0) parts.push(`${fileWord(size)} verified by size`);
    return parts.join(" · ");
  },
  diskFull: (done: number, total: number, written: number): string =>
    `Disk full after ${String(done)} of ${String(total)} files (${formatBytes(written)} written) — free space and press Resume.`,
  writeFailed: (
    fileName: string,
    done: number,
    total: number,
    detail: string | null,
  ): string =>
    `Could not write ${fileName} after ${String(done)} of ${String(total)} files${detail ? ` (${detail})` : ""} — press Resume to try again.`,
  stoppedByUser: (done: number, total: number): string =>
    `Stopped after ${String(done)} of ${String(total)} files — Resume continues where it left off.`,
  done: (files: number, present: number, written: number): string =>
    present > 0
      ? `Done · ${fileWord(files)} on disk (${fileWord(present)} were already there) · ${formatBytes(written)} written`
      : `Done · ${fileWord(files)} on disk · ${formatBytes(written)} written`,
  doneWithFailures: (failed: number): string =>
    `${fileWord(failed)} could not be downloaded — press Resume to try again.`,
  resume: "Resume",
  stop: "Stop",
  close: "Close",
  cancel: "Cancel",
  // DL-02 — the download manager (minimise, queue, status bar, the list)
  minimise: "Minimise",
  minimiseHint:
    "The download continues in the background — the bar at the bottom shows its progress.",
  queued: (position: number): string =>
    position <= 1
      ? "Starting…"
      : `Queued — ${String(position - 1)} ${position - 1 === 1 ? "download" : "downloads"} ahead of it.`,
  bar: (
    filesDone: number,
    filesTotal: number,
    setsCurrent: number,
    setsAll: number,
    bytes: number,
  ): string =>
    `Downloading · ${String(filesDone)} / ${String(filesTotal)} files` +
    (setsAll > 1
      ? ` · ${String(setsCurrent)} of ${String(setsAll)} data sets`
      : "") +
    ` · ${formatBytes(bytes)} written`,
  barStopped: (n: number): string =>
    `${String(n)} ${n === 1 ? "download" : "downloads"} stopped — click to see.`,
  // the pill (lower-left corner): short, the list has the detail
  pill: (
    filesDone: number,
    filesTotal: number,
    setsCurrent: number,
    setsAll: number,
  ): string =>
    `${String(filesDone)} / ${String(filesTotal)} files` +
    (setsAll > 1
      ? ` · ${String(setsCurrent)} of ${String(setsAll)} data sets`
      : ""),
  pillStopped: (n: number): string =>
    `${String(n)} ${n === 1 ? "download" : "downloads"} stopped`,
  pillDone: (files: number): string => `Done · ${fileWord(files)}`,
  barOpen: "Show downloads",
  managerTitle: "Downloads",
  managerEmpty: "No downloads in this tab.",
  remove: "Remove",
  removedFromList: "This download was removed from the list.",
  itemState: (
    state: "queued" | "running" | "paused" | "stopped" | "done",
    failures: number,
  ): string =>
    state === "queued"
      ? "Queued"
      : state === "running"
        ? "Downloading"
        : state === "paused"
          ? "Paused"
          : state === "stopped"
            ? "Stopped"
            : failures > 0
              ? "Done with failures"
              : "Done",
  pauseAll: "Pause all",
  resumeAll: "Resume all",
  stopAll: "Stop all",
  pausedHint:
    "Paused — nothing is being downloaded. Resume all continues where it left off; the list stays until you close it.",
  pillPaused: (filesDone: number, filesTotal: number): string =>
    `Paused · ${String(filesDone)} / ${String(filesTotal)} files`,
  itemLine: (
    state: "queued" | "running" | "paused" | "stopped" | "done",
    done: number,
    total: number,
    bytes: number,
    failures: number,
  ): string => {
    const files = `${String(done)} / ${String(total)} files`;
    switch (state) {
      case "queued":
        return `Queued · ${fileWord(total)}`;
      case "paused":
        return done > 0
          ? `Paused · ${files} · ${formatBytes(bytes)} written`
          : `Paused · ${fileWord(total)}`;
      case "running":
        return `Downloading · ${files} · ${formatBytes(bytes)} written`;
      case "stopped":
        return `Stopped · ${files} · ${formatBytes(bytes)} written`;
      case "done":
        return failures > 0
          ? `Done · ${files} · ${fileWord(failures)} could not be downloaded`
          : `Done · ${fileWord(done)} on disk · ${formatBytes(bytes)} written`;
    }
  },
  expiredRetried: "The download links expired and were renewed.",
  refusal: (r: DownloadRefusal): string => {
    switch (r.kind) {
      case "too_large":
        return `This download is too large${r.totalBytes !== null ? ` (${formatBytes(r.totalBytes)})` : ""} — the limit is ${formatBytes(r.limitBytes ?? LIMIT_BYTES)} per request. Split it: download per data set or per instruction.`;
      case "not_hot":
        return (
          r.message ??
          "These images are archived and cannot be downloaded any more — ask the project owner."
        );
      case "forbidden":
        return "You are not allowed to download the images of this data set.";
      case "not_found":
        return "This data set could not be found.";
      case "failed":
        return `The file list could not be read${r.status !== null ? ` (${String(r.status)})` : ""} — try again.`;
    }
  },
  progressLabel: "Image download progress",
} as const;
